// src/pages/Register.jsx
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  MenuItem,
  Grid,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Terminal as CoreIcon,
  Apps as AppAIIcon,
  Person as UserAIIcon,
  DesignServices as DesignIcon,
  DeveloperMode as DevIcon,
  RocketLaunch as DeployIcon,
  Info as InfoIcon
} from "@mui/icons-material";

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "",
    fullName: "",
    password: "",
    confirmPassword: "",
    organisationName: "",
    email: "",
    country: "",
    companyType: "",
    aiSystemStage: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  // Password validation on blur
  const handlePasswordBlur = () => {
    if (form.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
    } else {
      setPasswordError("");
    }
    // Also check confirm password if already filled
    if (form.confirmPassword && form.password !== form.confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
    } else if (form.confirmPassword) {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (form.confirmPassword.length < 8) {
      setConfirmPasswordError("Password must be at least 8 characters long");
    } else if (form.password !== form.confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError("");
    }
  };

  const countries = [
    "United States", "United Kingdom", "Canada", "Australia", "Germany", 
    "France", "Netherlands", "Switzerland", "Sweden", "Denmark", "Norway",
    "Singapore", "Japan", "South Korea", "India", "China", "Brazil",
    "Mexico", "Argentina", "South Africa", "UAE", "Other"
  ];

  const companyTypeInfo = [
    {
      key: "Core AI",
      icon: <CoreIcon sx={{ fontSize: 32 }} />,
      title: "Core AI Companies",
      desc: `Core AI companies focus on developing the foundational technologies and
infrastructure that power artificial intelligence.`
    },
    {
      key: "Application AI",
      icon: <AppAIIcon sx={{ fontSize: 32 }} />,
      title: "Application AI Companies",
      desc: `Application AI companies focus on developing AI-driven products and
solutions that apply across various industries.`
    },
    {
      key: "User AI",
      icon: <UserAIIcon sx={{ fontSize: 32 }} />,
      title: "User AI Companies",
      desc: `User AI companies focus on developing AI-driven technologies and applications
that directly interact with end users.`
    }
  ];

  const aiSystemStageInfo = [
    {
      key: "Design",
      icon: <DesignIcon sx={{ fontSize: 32 }} />,
      title: "Design Stage",
      desc: `An organization enters the design phase when it identifies and formulates the problem, and
explores, collects, and annotates data.`
    },
    {
      key: "Development",
      icon: <DevIcon sx={{ fontSize: 32 }} />,
      title: "Development Stage",
      desc: `An organization operates in the development phase during data preprocessing, model
training, testing, and evaluation.`
    },
    {
      key: "Deployment",
      icon: <DeployIcon sx={{ fontSize: 32 }} />,
      title: "Deployment Stage",
      desc: `An organization reaches the deployment phase when it operationalises the AI system,
integrates it into production environments, and subjects it to continuous monitoring, evaluation, and
oversight.`
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validateForm = () => {
    const requiredFields = ['username', 'password', 'confirmPassword', 'organisationName', 'email', 'country', 'companyType', 'aiSystemStage'];
    const missingFields = requiredFields.filter(field => !form[field]);
    
    if (missingFields.length > 0) {
      setError("Please fill in all required fields");
      return false;
    }

    if (form.username.length < 3) {
      setError("Username must be at least 3 characters long");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  // Selection box component for card-based selection
  const selectionBox = (item, selectedValue, fieldName) => (
    <Box
      key={item.key}
      onClick={() => setForm(prev => ({ ...prev, [fieldName]: item.key }))}
      sx={{
        cursor: "pointer",
        p: 2,
        borderRadius: 2,
        height: "100%",
        border: selectedValue === item.key ? "2px solid #004494" : "1px solid #ccc",
        backgroundColor: selectedValue === item.key ? "#e8f1ff" : "white",
        boxShadow: selectedValue === item.key ? 3 : 1,
        transition: "0.25s",
        "&:hover": {
          boxShadow: 3,
          transform: "scale(1.02)"
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start"
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        {item.icon}
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600, fontSize: '0.95rem' }}>
          {item.title}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          mt: 1.5,
          whiteSpace: "pre-line",
          color: "text.secondary",
          textAlign: "center",
          fontSize: '0.8rem',
          lineHeight: 1.3
        }}
      >
        {item.desc}
      </Typography>
    </Box>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // API payload to match the expected format
      const response = await fetch("https://py.taih.ai/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          full_name: form.fullName.trim(),
          user_id: form.username.trim(),
          password: form.password,
          email: form.email.trim().toLowerCase(),
          org_name: form.organisationName.trim(),
          contact_email: form.email.trim().toLowerCase(),
          country: form.country,
          company_type: form.companyType,
          company_type_code: form.companyType,
          current_stage: form.aiSystemStage,
          current_stage_code: form.aiSystemStage
        })
      });

      if (response.ok) {
        const userData = await response.json();
        
        console.log('Registration successful, received data:', userData);

        // Store session token - CRITICAL for application to work
        if (userData.session_token) {
          sessionStorage.setItem('sessionToken', userData.session_token);
          console.log('Session token saved:', userData.session_token);
        }

        // Store assessment ID if provided
        if (userData.assessment_id) {
          sessionStorage.setItem('assessmentId', userData.assessment_id.toString());
          console.log('Assessment ID saved:', userData.assessment_id);
        }

        // Store access link if provided
        if (userData.access_link) {
          sessionStorage.setItem('accessLink', userData.access_link);
          console.log('Access link saved:', userData.access_link);
        }

        // Store user registration data
        sessionStorage.setItem('userRegistrationData', JSON.stringify({
          username: form.username,
          fullName: form.fullName,
          organisationName: form.organisationName,
          email: form.email,
          country: form.country,
          companyType: form.companyType,
          aiSystemStage: form.aiSystemStage,
          isRegistered: true
        }));

        // Store comprehensive user data for the session
        sessionStorage.setItem('userData', JSON.stringify({
          user_pk: userData.user_pk,
          user_id: userData.user_id,
          full_name: form.fullName,
          is_admin: false, // Register API doesn't return this, so default to false
          status: 'active', // Default status
          org_name: form.organisationName,
          company_type: form.companyType,
          current_stage: form.aiSystemStage,
          country: form.country,
          assessment_id: userData.assessment_id,
          session_token: userData.session_token,
          access_link: userData.access_link
        }));

        console.log('All registration data stored successfully');

        // Redirect immediately to instructions page
        navigate("/assessment/instructions", { 
          state: { 
            registrationComplete: true,
            fromRegistration: true,
            userData: {
              username: form.username,
              fullName: form.fullName,
              organisationName: form.organisationName,
              email: form.email,
              country: form.country,
              companyType: form.companyType,
              aiSystemStage: form.aiSystemStage,
              sessionToken: userData.session_token,
              assessmentId: userData.assessment_id,
              accessLink: userData.access_link
            }
          }
        });
      } else {
        const errorData = await response.json();
        console.error('Registration failed:', errorData);
        setError(errorData.detail || errorData.message || "Registration failed. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
    
    // Note: We don't set loading to false in the success case because we're navigating away
  };

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        bgcolor: "background.default"
      }}
    >
      {/* Header */}
      <Box className="raise-blue" sx={{ py: 2 }}>
        <Container>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative',
            color: '#fff'
          }}>
            <IconButton 
              onClick={() => navigate('/')}
              sx={{ 
                color: 'white', 
                position: 'absolute',
                left: 0
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#fff' }}>
              <PersonAddIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff' }}>
                Create Your RAISE Index Account
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              maxWidth: 800,
              padding: 3,
              borderRadius: 2,
              textAlign: "center"
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, textAlign: 'left', mr: 1 }}>
                Join RAISE Index
              </Typography>
              <InfoIcon sx={{ color: '#004494', fontSize: 24, mr: 1 }} titleAccess="This tool is a work in progress." />
              <Typography variant="caption" sx={{ color: '#856404', bgcolor: '#fff3cd', px: 1, borderRadius: 1, fontWeight: 500 }}>
                This tool is a work in progress.
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary", textAlign: 'left' }}>
              Create your account to access expert-led Responsible AI assessments
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {/* Account Information Section */}
              <Box sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="h6" className="raise-blue-text" sx={{ mb: 2, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                  <LockIcon sx={{ mr: 1, fontSize: 20 }} />
                  Account Information
                </Typography>
                

                <TextField
                  name="username"
                  label="Username"
                  fullWidth
                  variant="outlined"
                  value={form.username}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  helperText="Minimum 3 characters (will be used as your user ID)"
                  required
                />

                <TextField
                  name="fullName"
                  label="Full Name"
                  fullWidth
                  variant="outlined"
                  value={form.fullName}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  helperText="Enter your full name"
                  required
                />

                <TextField
                  name="email"
                  label="Email Address"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={form.email}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  required
                />

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, width: '100%' }}>
                  <TextField
                    name="password"
                    label="Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={form.password}
                    onChange={handleInputChange}
                    onBlur={handlePasswordBlur}
                    disabled={loading}
                    helperText={passwordError ? passwordError : "Minimum 8 characters"}
                    error={!!passwordError}
                    required
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    name="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    variant="outlined"
                    value={form.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={handleConfirmPasswordBlur}
                    disabled={loading}
                    helperText={confirmPasswordError ? confirmPasswordError : ""}
                    error={!!confirmPasswordError}
                    required
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Organisation Information Section */}
              <Box sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="h6" className="raise-blue-text"  sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}>
                  <BusinessIcon sx={{ mr: 1, fontSize: 20 }} />
                  Organization Information
                </Typography>

                <TextField
                  name="organisationName"
                  label="Organization Name"
                  fullWidth
                  variant="outlined"
                  value={form.organisationName}
                  onChange={handleInputChange}
                  disabled={loading}
                  sx={{ mb: 2 }}
                  required
                />

                <FormControl 
                  fullWidth 
                  variant="outlined" 
                  required
                  sx={{ mb: 3 }}
                >
                  <InputLabel id="country-label">Country</InputLabel>
                  <Select
                    labelId="country-label"
                    name="country"
                    value={form.country}
                    onChange={handleInputChange}
                    label="Country"
                    disabled={loading}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* AI Company Type - Card Selection */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                  AI Company Type *
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 1.5,
                    mb: 3
                  }}
                >
                  {companyTypeInfo.map(item =>
                    selectionBox(item, form.companyType, "companyType")
                  )}
                </Box>

                {/* AI System Development Stage - Card Selection */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                  AI System Development Stage *
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 1.5
                  }}
                >
                  {aiSystemStageInfo.map(item =>
                    selectionBox(item, form.aiSystemStage, "aiSystemStage")
                  )}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ mt: 3, textAlign: "center" }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  size="large"
                  className="raise-blue"
                  sx={{ mb: 2, py: 1.5, color: '#fff', backgroundImage: 'none !important' }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account & Continue'
                  )}
                </Button>

                <Button
  component={Link}
  to="/login"
  fullWidth
  variant="outlined"
  disabled={loading}
  size="large"
  sx={{ 
    mb: 2,
    color: '#004494',
    borderColor: '#004494',
    '&:hover': {
      borderColor: '#003366',
      color: '#003366',
      backgroundColor: 'rgba(0, 68, 148, 0.04)'
    }
  }}
>
  Already have an account? Sign In
</Button>

                <Button
                  onClick={() => navigate('/')}
                  fullWidth
                  disabled={loading}
                  sx={{ color: 'text.secondary' }}
                >
                  Back to Home
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>

        {/* Additional Information */}
       <Paper 
  elevation={1} 
  sx={{ 
    p: 3, 
    mt: 3, 
    maxWidth: 800,
    mx: 'auto',
    bgcolor: 'background.accent',
    border: '1px solid',
    borderColor: 'divider',
    textAlign: 'center'
  }}
>
  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
    What Happens Next?
  </Typography>
  
  <Grid container spacing={3} justifyContent="center">
    <Grid item xs={12} sm={4}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#004494', mb: 1 }}>1</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Account Setup</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Complete your registration
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} sm={4}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#004494', mb: 1 }}>2</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Instructions</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Learn about the assessment process
        </Typography>
      </Box>
    </Grid>
    <Grid item xs={12} sm={4}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#004494', mb: 1 }}>3</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Assessment</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Complete your Responsible AI assessment
        </Typography>
      </Box>
    </Grid>
  </Grid>
</Paper>
      </Container>
    </Box>
  );
}